import { useEffect, useState } from 'react';

import {
  fetchCultureSelectorData,
  getSessionHandoffToken,
  persistUserSessionData,
  useCultureSelectorData,
} from '@skyscanner-internal/global-components/culture-selector/helpers';
import HeaderGC from '@skyscanner-internal/global-components/header';
import { loginModalInit } from '@skyscanner-internal/global-components/header/helpers';
import {
  SOURCES,
  type Source,
} from '@skyscanner-internal/global-components/login-form';
import {
  SubscriptionClient,
  useAutoPrompt,
} from '@skyscanner-internal/global-components/login-form/helpers';
import logger from 'saddlebag-logger';
import { isLoggedIn, getProfilePicture } from 'saddlebag-user-management';

import { isBrowser } from '../../utils/isEnv';
import {
  publishLoginAutoPromptClosed,
  publishLoginAutoPromptOpened,
} from '../../utils/loginPromptEvents';
import withErrorBoundary from '../ErrorBoundary/WithErrorBoundary';

import { useAutoPromptGate } from './hooks/useAutoPromptGate';

import type { HeaderProps as HeaderGCProps } from '@skyscanner-internal/global-components/header';
import type { PartialAuthClient } from '@skyscanner-internal/global-components/login-form/types/auth-client';
import type { HeaderProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/Header';

type Nullable<T> = T | null;

const Header = ({
  autoPromptStringOverrides,
  enableLoginFormAutoPromptSEO,
  pageType,
  ...props
}: HeaderProps) => {
  const [isLoginFormVisible, setIsLoginFormVisible] = useState(false);
  const [wasAutoPrompted, setWasAutoPrompted] = useState(false);

  const [authClient, setAuthClient] =
    useState<Nullable<PartialAuthClient>>(null);
  const [subscriptionClient, setSubscriptionClient] =
    useState<Nullable<SubscriptionClient>>(null);

  const canAutoPrompt = useAutoPromptGate({ market: props.market });

  useAutoPrompt({
    isFeatureEnabled: enableLoginFormAutoPromptSEO,
    canPrompt: canAutoPrompt,
    logger,
    onShouldOpen: () => {
      setWasAutoPrompted(true);
      setIsLoginFormVisible(true);
      publishLoginAutoPromptOpened();
    },
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      loginModalInit({ setLoginFormVisible: setIsLoginFormVisible });

      // eslint-disable-next-line global-require
      const AuthClient = require('@skyscanner/auth-client').default;

      // Initialise non-critical authentication clients after the initial render.
      setSubscriptionClient(new SubscriptionClient());

      setAuthClient(
        new AuthClient({
          locale: props.locale,
          market: props.market,
          authConsumer: 'falcon',
          env: props.isProductionEnv ? 'production' : 'development',
        }),
      );
    }, 0);

    return () => clearTimeout(timeout);
  }, [props.locale, props.market, props.isProductionEnv]);

  const changeLoginFormVisibility = ({
    shouldBeOpen,
  }: {
    shouldBeOpen?: boolean;
    loginFlowComplete?: boolean;
  }) => {
    setIsLoginFormVisible(Boolean(shouldBeOpen));
    if (!shouldBeOpen) {
      if (wasAutoPrompted) {
        publishLoginAutoPromptClosed();
      }
      setWasAutoPrompted(false);
    }
  };

  const {
    currencies,
    enMarkets,
    isFetchingCultureSelectorData,
    locales,
    markets,
    onCultureSelectorButtonClick,
  } = useCultureSelectorData(
    fetchCultureSelectorData,
    props.locale ?? 'en-GB',
    false,
  );

  const {
    cultureSelectorEnStrings,
    cultureSelectorStrings,
    // eslint-disable-next-line camelcase
    unstable__staysNewBubble,
    ...filteredProps
  } = props;

  const userLoggedIn = isBrowser() ? isLoggedIn() : undefined;
  const profilePictureFn = isBrowser() ? getProfilePicture : undefined;

  const experimentContext = enableLoginFormAutoPromptSEO
    ? 'SEO_AUTO_PROMPT'
    : undefined;

  const headerGCProps: HeaderGCProps = {
    logger,
    userLoggedIn,
    getProfilePicture: profilePictureFn,
    ...filteredProps,
    cultureSelectorProps: {
      markets,
      locales,
      currencies,
      enMarkets,
      isProductionEnv: props.isProductionEnv,
      strings: cultureSelectorStrings,
      enStrings: cultureSelectorEnStrings,
      persistUserSessionDataFn:
        props.enableAnonymousUtidPropagationOnCultureSelectorTldRedirect
          ? persistUserSessionData
          : undefined,
      getSessionHandoffTokenFn: getSessionHandoffToken,
    },
    loginFormProps:
      authClient && subscriptionClient
        ? {
            strings: wasAutoPrompted
              ? { ...props.loginFormStrings, ...autoPromptStringOverrides }
              : props.loginFormStrings,
            isProductionEnv: props.isProductionEnv,
            authClient,
            isOpen: isLoginFormVisible,
            onVisibilityChange: changeLoginFormVisibility,
            subscriptionClient,
            source: (wasAutoPrompted
              ? SOURCES.AUTO_PROMPT
              : SOURCES.HEADER) as Source,
            consumer: 'falcon',
            screenName: pageType,
            experimentContext,
          }
        : undefined,
    isFetchingCultureSelectorData,
    onCultureSelectorButtonClick,
    // eslint-disable-next-line camelcase
    unstable__hotelsNewBubble: unstable__staysNewBubble,
  };

  return (
    <div id="header-root">
      <HeaderGC {...headerGCProps} />
    </div>
  );
};

const HeaderWithErrorBoundary = withErrorBoundary(Header, 'header');

export default HeaderWithErrorBoundary;
